import React, { useState, useRef } from "react"
import { Modal } from "reactstrap"

// ── Inline SVG icons (no lucide-react dependency) ─────────────────────────────
const PlayIcon = ({
  size = 12,
  color = "currentColor",
  fill = "currentColor",
  style,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={fill}
    stroke="none"
    style={style}
  >
    <polygon points="5 3 19 12 5 21 5 3" />
  </svg>
)

const ChevronRightIcon = ({ size = 14, color = "currentColor", style }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
)

const CloseIcon = ({ size = 16, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.5"
    strokeLinecap="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
)

const VideoIcon = ({ size = 18, color = "currentColor" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="2" y="3" width="15" height="15" rx="2" />
    <path d="M17 8l4-2v10l-4-2V8z" />
  </svg>
)

// ── Video List Item ────────────────────────────────────────────────────────────
const VideoListItem = ({ doc, idx, isActive, onClick }) => (
  <li
    className={`vl-item${isActive ? " vl-item--active" : ""}`}
    style={{ animationDelay: `${idx * 0.05}s` }}
  >
    <button className="vl-item-btn" onClick={onClick} aria-pressed={isActive}>
      <div className="vl-item-icon">
        <PlayIcon
          size={10}
          fill={isActive ? "#fff" : "rgba(4,120,87,0.7)"}
          style={{ marginLeft: "2px" }}
        />
      </div>
      <span className="vl-item-title">{doc.name}</span>
      {isActive && <ChevronRightIcon size={13} color="rgba(5,150,105,0.7)" />}
    </button>
  </li>
)

// ── Main Component ─────────────────────────────────────────────────────────────
const VideoLibraryModal = ({ rc, onOpenRC, Alldata, curRC }) => {
  const areaData = Alldata?.data?.videolibrary?.data?.areas?.[curRC]
  const [activeVideoUrl, setActiveVideoUrl] = useState(
    areaData?.data?.[0]?.url || ""
  )
  const [activeVideoName, setActiveVideoName] = useState(
    areaData?.data?.[0]?.name || ""
  )
  const iframeRef = useRef(null)

  const handleVideoSelect = doc => {
    setActiveVideoUrl(doc.url)
    setActiveVideoName(doc.name)
    if (iframeRef.current) iframeRef.current.src = doc.url
  }

  const totalVideos = areaData?.data?.length || 0

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&family=DM+Mono:wght@400;500&display=swap');

        /* ── Keyframes: GPU-only (opacity + transform) ── */
        @keyframes vl-fadein {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes vl-pulse-dot {
          0%, 100% { opacity: 1;   transform: scale(1); }
          50%       { opacity: 0.4; transform: scale(0.7); }
        }
        @keyframes vl-shimmer {
          0%   { transform: translateX(-120%); }
          100% { transform: translateX(220%); }
        }

        /* ── Modal shell ── */
        .vl-modal .modal-dialog {
          max-width: min(860px, 96vw);
          margin: 12px auto;
        }
        .vl-modal .modal-content {
          background: transparent;
          border: none;
          border-radius: 14px;
          overflow: hidden;
          font-family: 'DM Sans', sans-serif;
        }

        /* ── Root card ── */
        .vl-card {
          background: #f7faf9;
          border-radius: 14px;
          border: 1px solid rgba(4,120,87,0.18);
          box-shadow: 0 20px 60px rgba(0,60,40,0.18), 0 4px 16px rgba(0,0,0,0.08);
          overflow: hidden;
          animation: vl-fadein 0.28s ease both;
        }

        /* ── Header ── */
        .vl-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 18px 12px;
          background: linear-gradient(135deg, #005b43 0%, #005b43 55%, #288f70 100%);
          border-bottom: 1px solid rgba(0,0,0,0.1);
          position: relative;
          overflow: hidden;
        }
        /* Subtle moving sheen — CSS only, no JS */
        .vl-header::after {
          content: '';
          position: absolute;
          top: 0; bottom: 0;
          left: 0; width: 40%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.07), transparent);
          transform: translateX(-120%);
          animation: vl-shimmer 5s ease-in-out infinite;
          pointer-events: none;
          will-change: transform;
        }
        .vl-header-left {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }
        .vl-header-icon {
          width: 34px;
          height: 34px;
          border-radius: 8px;
          background: rgba(255,255,255,0.14);
          border: 1px solid rgba(255,255,255,0.22);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .vl-header-text { min-width: 0; }
        .vl-header-label {
          font-size: 9.5px;
          font-weight: 600;
          letter-spacing: 2.5px;
          text-transform: uppercase;
          color: rgba(167,243,208,0.85);
          font-family: 'DM Mono', monospace;
          display: block;
          line-height: 1;
          margin-bottom: 2px;
        }
        .vl-header-title {
          font-size: 16px;
          font-weight: 700;
          color: #ffffff;
          letter-spacing: 0.2px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          display: block;
        }
        .vl-close-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: rgba(255,255,255,0.1);
          border: 1px solid rgba(255,255,255,0.2);
          color: rgba(255,255,255,0.85);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.18s, border-color 0.18s;
          flex-shrink: 0;
        }
        .vl-close-btn:hover {
          background: rgba(255,255,255,0.22);
          border-color: rgba(255,255,255,0.4);
        }

        /* ── Body grid ── */
        .vl-body {
          display: grid;
          grid-template-columns: 1fr 224px;
          min-height: 0;
        }

        /* ── Player pane ── */
        .vl-player-pane {
          padding: 16px 16px 16px 18px;
          border-right: 1px solid rgba(4,120,87,0.1);
          display: flex;
          flex-direction: column;
          gap: 11px;
          min-width: 0;
        }
        .vl-now-playing {
          display: flex;
          align-items: center;
          gap: 7px;
          min-width: 0;
          height: 18px;
        }
        .vl-pulse-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: #059669;
          box-shadow: 0 0 6px rgba(5,150,105,0.55);
          flex-shrink: 0;
          animation: vl-pulse-dot 2s ease-in-out infinite;
          will-change: transform, opacity;
        }
        .vl-np-label {
          font-size: 9.5px;
          font-weight: 600;
          letter-spacing: 1.8px;
          text-transform: uppercase;
          color: #059669;
          font-family: 'DM Mono', monospace;
          flex-shrink: 0;
        }
        .vl-np-title {
          font-size: 13px;
          font-weight: 600;
          color: #1a3a2e;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 0;
        }
        .vl-iframe-wrap {
          position: relative;
          width: 100%;
          padding-top: 56.25%;
          border-radius: 9px;
          overflow: hidden;
          background: #0d1f17;
          border: 1px solid rgba(4,120,87,0.22);
          box-shadow: 0 4px 20px rgba(0,60,40,0.14);
        }
        .vl-iframe-wrap iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: none;
        }
        .vl-player-divider {
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(5,150,105,0.28), transparent);
        }

        /* ── List pane ── */
        .vl-list-pane {
          background: #edf7f2;
          padding: 14px 10px 14px 12px;
          display: flex;
          flex-direction: column;
          gap: 8px;
          min-width: 0;
          border-left: 1px solid rgba(4,120,87,0.08);
        }
        .vl-list-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding-bottom: 10px;
          border-bottom: 1px solid rgba(4,120,87,0.12);
          flex-shrink: 0;
        }
        .vl-list-label {
          font-size: 9.5px;
          font-weight: 600;
          letter-spacing: 2px;
          text-transform: uppercase;
          color: #047857;
          font-family: 'DM Mono', monospace;
        }
        .vl-count-badge {
          font-size: 10px;
          font-weight: 500;
          font-family: 'DM Mono', monospace;
          color: #047857;
          background: rgba(5,150,105,0.1);
          border: 1px solid rgba(5,150,105,0.22);
          border-radius: 20px;
          padding: 1px 8px;
        }

        /* ── Scrollable list ── */
        .vl-scroll {
          margin: 0;
          padding: 0;
          list-style: none;
          overflow-y: auto;
          flex: 1;
          max-height: 340px;
          padding-right: 2px;
        }
        .vl-scroll::-webkit-scrollbar { width: 3px; }
        .vl-scroll::-webkit-scrollbar-track { background: transparent; }
        .vl-scroll::-webkit-scrollbar-thumb { background: rgba(4,120,87,0.35); border-radius: 2px; }

        /* ── List item ── */
        .vl-item { animation: vl-fadein 0.28s ease both; }
        .vl-item-btn {
          width: 100%;
          background: transparent;
          border: 1px solid transparent;
          border-radius: 7px;
          padding: 8px 9px;
          margin-bottom: 3px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          text-align: left;
          transition: background 0.16s ease, border-color 0.16s ease;
        }
        .vl-item-btn:hover {
          background: rgba(5,150,105,0.07);
          border-color: rgba(5,150,105,0.18);
        }
        .vl-item--active .vl-item-btn {
          background: linear-gradient(135deg, rgba(5,150,105,0.13), rgba(4,120,87,0.06));
          border-color: rgba(5,150,105,0.35);
          box-shadow: 0 2px 8px rgba(5,150,105,0.08);
        }
        .vl-item-icon {
          width: 25px;
          height: 25px;
          border-radius: 50%;
          background: rgba(5,150,105,0.1);
          border: 1px solid rgba(5,150,105,0.2);
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: background 0.16s, border-color 0.16s;
        }
        .vl-item--active .vl-item-icon {
          background: linear-gradient(135deg, #047857, #059669);
          border-color: #34d399;
          box-shadow: 0 0 7px rgba(5,150,105,0.35);
        }
        .vl-item-title {
          font-size: 12px;
          font-weight: 500;
          color: #374151;
          line-height: 1.35;
          flex: 1;
          transition: color 0.14s;
        }
        .vl-item--active .vl-item-title {
          font-weight: 600;
          color: #065f46;
        }

        /* ── Footer bar ── */
        .vl-footer {
          height: 3px;
          background: linear-gradient(90deg, #065f46 0%, #059669 40%, #34d399 60%, #059669 80%, #065f46 100%);
        }

        /* ── Responsive ── */
        @media (max-width: 600px) {
          .vl-modal .modal-dialog {
            margin: 6px;
            max-width: calc(100vw - 12px);
          }
          .vl-body {
            grid-template-columns: 1fr;
          }
          .vl-player-pane {
            padding: 13px;
            border-right: none;
            border-bottom: 1px solid rgba(4,120,87,0.1);
          }
          .vl-list-pane {
            border-left: none;
            border-top: 1px solid rgba(4,120,87,0.08);
            padding: 12px 10px;
          }
          .vl-scroll { max-height: 190px; }
          .vl-header-title { font-size: 14px; }
        }
        @media (max-width: 380px) {
          .vl-header-label { display: none; }
          .vl-header { padding: 10px 13px; }
          .vl-header-icon { display: none; }
        }
      `}</style>

      <Modal
        size="xl"
        isOpen={rc}
        toggle={onOpenRC}
        centered
        className="vl-modal"
        style={{ overflowY: "auto" }}
      >
        <div className="vl-card">
          {/* ── Header ── */}
          <div className="vl-header">
            <div className="vl-header-left">
              <div className="vl-header-icon">
                <VideoIcon size={17} color="rgba(255,255,255,0.9)" />
              </div>
              <div className="vl-header-text">
                <span className="vl-header-label">Video Library</span>
                <span className="vl-header-title">
                  {"Videos"}
                </span>
              </div>
            </div>
            <button
              className="vl-close-btn"
              onClick={onOpenRC}
              aria-label="Close"
            >
              <CloseIcon size={15} color="rgba(255,255,255,0.9)" />
            </button>
          </div>

          {/* ── Body ── */}
          <div className="vl-body">
            {/* Player */}
            <div className="vl-player-pane">
              {activeVideoName && (
                <div className="vl-now-playing">
                  <div className="vl-pulse-dot" />
                  <span className="vl-np-label">Now Playing</span>
                  <span className="vl-np-title">{activeVideoName}</span>
                </div>
              )}
              <div className="vl-iframe-wrap">
                <iframe
                  ref={iframeRef}
                  id={areaData?.name}
                  src={activeVideoUrl}
                  title={activeVideoName}
                  allowFullScreen
                />
              </div>
              <div className="vl-player-divider" />
            </div>

            {/* Playlist */}
            <div className="vl-list-pane">
              <div className="vl-list-header">
                <span className="vl-list-label">Playlist</span>
                <span className="vl-count-badge">{totalVideos}</span>
              </div>
              <ul className="vl-scroll">
                {areaData?.data?.map((doc, ind) => (
                  <VideoListItem
                    key={ind}
                    doc={doc}
                    idx={ind}
                    isActive={activeVideoUrl === doc.url}
                    onClick={() => handleVideoSelect(doc)}
                  />
                ))}
              </ul>
            </div>
          </div>

          {/* ── Footer accent ── */}
          <div className="vl-footer" />
        </div>
      </Modal>
    </>
  )
}

export default VideoLibraryModal
